import { supabase } from '@/lib/supabase'
import type {
  AssistanceRequestWithDetails,
  AssistanceFilters,
  ContractWithCoverages,
  ContractCoverageRow,
} from '@/types/assistance'
import { DEFAULT_COVERAGE_TYPES } from '@/types/assistance'

export const assistanceService = {
  // ============================================
  // ASSISTANCE REQUESTS
  // ============================================

  async getAll(
    filters?: AssistanceFilters,
    page = 1,
    limit = 20
  ): Promise<{ requests: AssistanceRequestWithDetails[]; total: number }> {
    let query = supabase
      .from('assistance_requests')
      .select(`
        *,
        project:projects!assistance_requests_project_id_fkey(id, title, user_id),
        user:users!assistance_requests_user_id_fkey(id, full_name, email),
        assigned_admin:users!assistance_requests_assigned_to_fkey(id, full_name, email)
      `, { count: 'exact' })
      .order('created_at', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.priority) {
      query = query.eq('priority', filters.priority)
    }
    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
    }
    if (filters?.projectId) {
      query = query.eq('project_id', filters.projectId)
    }
    if (filters?.isCovered === 'true') {
      query = query.eq('is_covered', true)
    } else if (filters?.isCovered === 'false') {
      query = query.eq('is_covered', false)
    }

    const from = (page - 1) * limit
    const to = from + limit - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw error

    return {
      requests: (data || []) as unknown as AssistanceRequestWithDetails[],
      total: count || 0,
    }
  },

  async getById(id: string): Promise<AssistanceRequestWithDetails | null> {
    const { data, error } = await supabase
      .from('assistance_requests')
      .select(`
        *,
        project:projects!assistance_requests_project_id_fkey(id, title, user_id),
        user:users!assistance_requests_user_id_fkey(id, full_name, email),
        assigned_admin:users!assistance_requests_assigned_to_fkey(id, full_name, email)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as unknown as AssistanceRequestWithDetails
  },

  async getByProjectId(projectId: string): Promise<AssistanceRequestWithDetails[]> {
    const { data, error } = await supabase
      .from('assistance_requests')
      .select(`
        *,
        project:projects!assistance_requests_project_id_fkey(id, title, user_id),
        user:users!assistance_requests_user_id_fkey(id, full_name, email),
        assigned_admin:users!assistance_requests_assigned_to_fkey(id, full_name, email)
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data || []) as unknown as AssistanceRequestWithDetails[]
  },

  async update(id: string, updates: Record<string, any>): Promise<AssistanceRequestWithDetails> {
    const payload: Record<string, any> = {
      ...updates,
      updated_at: new Date().toISOString(),
    }

    if (updates.status === 'completed' || updates.status === 'rejected') {
      payload.resolved_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('assistance_requests')
      .update(payload)
      .eq('id', id)
      .select(`
        *,
        project:projects!assistance_requests_project_id_fkey(id, title, user_id),
        user:users!assistance_requests_user_id_fkey(id, full_name, email),
        assigned_admin:users!assistance_requests_assigned_to_fkey(id, full_name, email)
      `)
      .single()

    if (error) throw error
    return data as unknown as AssistanceRequestWithDetails
  },

  async getStats(): Promise<{
    total: number
    submitted: number
    in_progress: number
    billable: number
  }> {
    const { data, error } = await supabase
      .from('assistance_requests')
      .select('status, is_covered')

    if (error) throw error

    return {
      total: data?.length || 0,
      submitted: data?.filter(r => r.status === 'submitted').length || 0,
      in_progress: data?.filter(r => r.status === 'in_progress' || r.status === 'approved').length || 0,
      billable: data?.filter(r => r.is_covered === false && r.status !== 'rejected').length || 0,
    }
  },

  async getSubmittedCount(): Promise<number> {
    const { count, error } = await supabase
      .from('assistance_requests')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'submitted')

    if (error) throw error
    return count || 0
  },

  // ============================================
  // CONTRACTS
  // ============================================

  async getContractByProjectId(projectId: string): Promise<ContractWithCoverages | null> {
    const { data, error } = await supabase
      .from('contracts')
      .select(`
        *,
        contract_coverages(*)
      `)
      .eq('project_id', projectId)
      .in('status', ['active', 'paused'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw error
    return data as unknown as ContractWithCoverages | null
  },

  async createContract(contractData: Record<string, any>): Promise<ContractWithCoverages> {
    const { data, error } = await supabase
      .from('contracts')
      .insert(contractData)
      .select(`
        *,
        contract_coverages(*)
      `)
      .single()

    if (error) throw error
    return data as unknown as ContractWithCoverages
  },

  async updateContract(id: string, updates: Record<string, any>): Promise<ContractWithCoverages> {
    const { data, error } = await supabase
      .from('contracts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(`
        *,
        contract_coverages(*)
      `)
      .single()

    if (error) throw error
    return data as unknown as ContractWithCoverages
  },

  // ============================================
  // CONTRACT COVERAGES
  // ============================================

  async getCoverages(contractId: string): Promise<ContractCoverageRow[]> {
    const { data, error } = await supabase
      .from('contract_coverages')
      .select('*')
      .eq('contract_id', contractId)
      .order('coverage_type')

    if (error) throw error
    return (data || []) as ContractCoverageRow[]
  },

  async upsertCoverage(coverage: Partial<ContractCoverageRow> & { contract_id: string; coverage_type: string; label: string }): Promise<ContractCoverageRow> {
    const payload = {
      ...coverage,
      updated_at: new Date().toISOString(),
    }

    if (coverage.id) {
      const { data, error } = await supabase
        .from('contract_coverages')
        .update(payload)
        .eq('id', coverage.id)
        .select()
        .single()
      if (error) throw error
      return data as ContractCoverageRow
    } else {
      const { data, error } = await supabase
        .from('contract_coverages')
        .insert(payload)
        .select()
        .single()
      if (error) throw error
      return data as ContractCoverageRow
    }
  },

  async deleteCoverage(id: string): Promise<void> {
    const { error } = await supabase
      .from('contract_coverages')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async initializeDefaultCoverages(contractId: string): Promise<ContractCoverageRow[]> {
    const rows = DEFAULT_COVERAGE_TYPES.map(ct => ({
      contract_id: contractId,
      coverage_type: ct.coverage_type,
      label: ct.label,
      is_covered: false,
    }))

    const { data, error } = await supabase
      .from('contract_coverages')
      .insert(rows)
      .select()

    if (error) throw error
    return (data || []) as ContractCoverageRow[]
  },
}
